import React from 'react';
import { Link } from 'react-router-dom';
import ArticlePage from './ArticlePage';

function OptimizationPage() {
  const articleContent = (
    <>
      <h2>The Gap Between Understanding and Acting</h2>
      <p>
        Simulation answers the question: "What happens if we do X?"
      </p>
      <p>
        But leaders don't just need to explore possibilities. They need to find the best one.
      </p>
      <p>
        That's where optimization comes in—not as a replacement for simulation, but as its natural extension. 
        Simulation builds understanding. Optimization searches for the decision that performs best 
        across the scenarios that matter.
      </p>

      <h2>What Optimization Actually Means in Practice</h2>
      <p>
        In business contexts, optimization is rarely about finding a single perfect answer. It's about:
      </p>
      <ul>
        <li>Identifying which levers have the most impact</li>
        <li>Finding configurations that balance competing objectives</li>
        <li>Stress-testing candidate decisions before commitment</li>
        <li>Ruling out options that fail under realistic conditions</li>
      </ul>
      <p>
        The goal isn't mathematical perfection. It's confident, defensible action.
      </p>

      <h2>Why Spreadsheet Optimization Falls Short</h2>
      <p>
        Traditional optimization tools assume:
      </p>
      <ul>
        <li>Linear relationships between inputs and outputs</li>
        <li>Static constraints that don't shift over time</li>
        <li>A single objective to maximize or minimize</li>
        <li>Perfect information about parameters</li>
      </ul>
      <p>
        Real business systems violate all of these. Feedback loops create nonlinearity. 
        Constraints emerge dynamically. Leaders balance cost, speed, risk, and capacity simultaneously. 
        And <Link to="/innovation/uncertainty" className="text-gray-900 underline hover:text-gray-600">uncertainty</Link> is the norm, not the exception.
      </p>
      <p>
        This is why optimization must be coupled with simulation—exploring how decisions perform 
        across realistic, dynamic scenarios rather than static snapshots.
      </p>

      <div className="my-8">
        <img 
          src="/images/optimization-square.png" 
          alt="Multi-Objective Optimization" 
          className="w-full h-auto rounded-lg"
          loading="lazy"
        />
      </div>

      <h2>Multi-Objective Optimization</h2>
      <p>
        Most strategic decisions involve tradeoffs:
      </p>
      <ul>
        <li>Lower cost vs. faster delivery</li>
        <li>Higher throughput vs. lower risk</li>
        <li>Short-term savings vs. long-term flexibility</li>
      </ul>
      <p>
        Multi-objective optimization doesn't pretend these tradeoffs don't exist. Instead, it maps 
        the frontier of possibilities—showing decision-makers what's achievable and what they're 
        giving up with each choice.
      </p>
      <p>
        This shifts the conversation from "find me the answer" to "here are the best options and 
        their tradeoffs."
      </p>

      <h2>Robust Optimization Under Uncertainty</h2>
      <p>
        A decision that looks optimal under one scenario may fail badly under another. 
        Robust optimization seeks decisions that perform well across a range of plausible futures—not 
        just the expected case.
      </p>
      <p>
        Key questions robust optimization helps answer:
      </p>
      <ul>
        <li>Which decision holds up if demand is 20% lower than forecast?</li>
        <li>What's the downside if a key assumption is wrong?</li>
        <li>Which option has the best worst-case performance?</li>
        <li>Where do we need contingency plans?</li>
      </ul>

      <h2>The Human in the Loop</h2>
      <p>
        Optimization in complex systems isn't about handing control to an algorithm. It's about 
        augmenting human judgment.
      </p>
      <p>
        Effective optimization workflows:
      </p>
      <ul>
        <li>Let humans define objectives and constraints</li>
        <li>Surface surprising or counterintuitive options</li>
        <li>Explain why certain configurations perform well or poorly</li>
        <li>Enable rapid iteration as understanding deepens</li>
      </ul>
      <p>
        The algorithm explores the possibility space. Humans choose based on context, values, and 
        judgment that no model can fully capture.
      </p>

      <h2>When Optimization Adds the Most Value</h2>
      <p>
        Optimization shines when:
      </p>
      <ul>
        <li>The decision space is large and manual search is impractical</li>
        <li>Multiple objectives must be balanced explicitly</li>
        <li>Stakes are high enough to justify rigorous analysis</li>
        <li>The system is well enough understood to simulate meaningfully</li>
      </ul>
      <p>
        It's less valuable when the real challenge is <Link to="/innovation/system-dynamics" className="text-gray-900 underline hover:text-gray-600">understanding the system</Link> in 
        the first place, or when political and organizational factors dominate the decision.
      </p>

      <h2>The Complete Decision Stack</h2>
      <p>
        Bringing it together:
      </p>
      <p>
        <strong>1. System Understanding</strong><br />
        Map the <Link to="/innovation/feedback-loops" className="text-gray-900 underline hover:text-gray-600">feedback loops</Link>, delays, and relationships that drive outcomes.
      </p>
      <p>
        <strong>2. Simulation</strong><br />
        Explore how the system behaves under different conditions and decisions.
      </p>
      <p>
        <strong>3. Uncertainty Quantification</strong><br />
        Capture what you don't know and how it affects projections.
      </p>
      <p>
        <strong>4. Optimization</strong><br />
        Search for decisions that perform well across objectives and scenarios.
      </p>
      <p>
        <strong>5. Decision Intelligence</strong><br />
        Choose and act with clarity about tradeoffs and risks.
      </p>
      <p>
        Each layer builds on the one before. Skip a layer, and the next one becomes unreliable.
      </p>

      <h2>Moving Forward</h2>
      <p>
        Organizations that combine simulation with optimization don't just make faster decisions. 
        They make decisions they can explain, defend, and adapt as conditions change.
      </p>
      <p>
        The path forward isn't about finding the perfect answer. It's about finding the best 
        answer you can confidently act on—and knowing why it's the best.
      </p>
    </>
  );

  const relatedArticles = [
    { category: 'Article', title: 'Quantifying Uncertainty in Strategic Models', href: '/innovation/uncertainty' },
    { category: 'Article', title: 'System Dynamics in Enterprise Decision Making', href: '/innovation/system-dynamics' },
    { category: 'Article', title: 'From Operational Intelligence to Decision Intelligence', href: '/innovation/decision-intelligence' },
  ];

  return (
    <ArticlePage
      category="Article"
      title="Using Optimization with Simulation to Find the Best Path Forward"
      subtitle="How to move from exploring possibilities to identifying the decisions that perform best across competing objectives and uncertain conditions."
      heroImage="/images/optimization-hero.png"
      articleContent={articleContent}
      relatedArticles={relatedArticles}
    />
  );
}

export default OptimizationPage;
